import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Payment, PaymentType, Txn } from '../../types';
import { fetchPaymentsByTxnId } from '../../api';
import { TxnConfig } from './TxnConfig';

interface DocumentTrailProps {
    txn: Txn;
    txnConfig?: TxnConfig | null;
}

// MBAI-518 — "Document Trail" section for the transaction detail/view screen.
//
// Renders a breadcrumb-style chain of linked documents: source doc -> this doc -> payment(s).
// Reuses two data sources that already exist in the app rather than inventing a new pattern:
//   - Backward link: txnHeader.convertedFromTxnId/convertedFromTxnType (MBAI-324). This is already
//     persisted by the backend and already rendered as a badge inside TxnForm.tsx's edit header,
//     but was never surfaced on the read-only ViewTxn.tsx detail screen — this component fixes that.
//   - Forward link to payments: fetchPaymentsByTxnId (already used by TxnPaymentsList) for
//     payment-related txn types (Bills, Invoices, etc.)
//
// KNOWN GAP (see MBAI-518 Jira comment): there is no backend support for the reverse lookup —
// "which documents were converted FROM this one" (e.g. which Bills were created from a given
// Purchase Order). Only the reverse direction (a Bill's own source PO) is persisted. So a
// Purchase Order's trail cannot show its downstream Bills yet; this component simply renders
// nothing extra in that case rather than guessing via an unbounded client-side scan.
export const DocumentTrail: React.FC<DocumentTrailProps> = ({ txn, txnConfig }) => {
    const navigate = useNavigate();
    const [payments, setPayments] = useState<Payment[]>([]);
    const txnId = txn?.txnHeader?.txnId;
    const convertedFromTxnId = txn?.txnHeader?.convertedFromTxnId;
    const convertedFromTxnType = txn?.txnHeader?.convertedFromTxnType;

    useEffect(() => {
        let active = true;
        if (txnConfig?.paymentRelated && txnId) {
            fetchPaymentsByTxnId(txnId).then((data) => {
                if (active) setPayments(data || []);
            }).catch(() => {
                // TxnPaymentsList already surfaces a toast for this same fetch — stay silent here
                // to avoid a duplicate error message for one failed call.
            });
        } else {
            setPayments([]);
        }
        return () => { active = false; };
    }, [txnId, txnConfig?.paymentRelated]);

    const hasBackLink = Boolean(convertedFromTxnId && convertedFromTxnType);
    const hasForwardLinks = payments.length > 0;

    if (!hasBackLink && !hasForwardLinks) {
        return null;
    }

    return (
        <div className="d-flex align-items-center flex-wrap gap-2 mb-4">
            <span className="text-muted fw-semibold fs-8 me-1">
                <i className="bi bi-diagram-3 me-1" />
                {'Document Trail:'.toTranslatedDisplay()}
            </span>
            {hasBackLink && (
                <>
                    <span
                        className="badge badge-light-info cursor-pointer"
                        title={'Click to view the source document'.toTranslatedDisplay()}
                        onClick={() => navigate(`/transactions/view-txn/${convertedFromTxnType}/${convertedFromTxnId}`)}
                    >
                        <i className="bi bi-link-45deg me-1" />
                        {(convertedFromTxnType || '').toTranslatedDisplay()}
                    </span>
                    <i className="bi bi-arrow-right text-muted" />
                </>
            )}
            <span className="badge badge-light-primary">
                {(txn.txnHeader.txnType || '').toTranslatedDisplay()}{txn.txnHeader.txnNo ? ` #${txn.txnHeader.txnNo}` : ''}
            </span>
            {hasForwardLinks && payments.map((p) => (
                <span key={p.paymentHeader.paymentId} className="d-flex align-items-center gap-2">
                    <i className="bi bi-arrow-right text-muted" />
                    <span
                        className="badge badge-light-success cursor-pointer"
                        title={'Click to view payment'.toTranslatedDisplay()}
                        onClick={() => navigate(`/transactions/view-payment/${p.paymentHeader.paymentType || PaymentType.VendorPayment}/${p.paymentHeader.paymentId}`)}
                    >
                        <i className="bi bi-credit-card me-1" />
                        {p.paymentHeader.paymentNo || 'Payment'.toTranslatedDisplay()}
                    </span>
                </span>
            ))}
        </div>
    );
};
